/**
 * Personality traits for drivers. Each trait declares:
 *   - label (FR)
 *   - refuses: array of training type IDs the driver will categorically REFUSE
 *     (hard block — UI shows "Refusera" in red, click is disabled)
 *   - malus: array of training type IDs the driver will accept but with a
 *     -8 satisfaction hit (soft conflict — UI shows "Maugréera (-8 sat)" in
 *     amber, training proceeds normally)
 *   - dailyImpact: { likes: string, dislikes: string } — FR description of
 *     what the trait makes the driver enjoy or dread on a daily basis. Only
 *     surfaced to the player AFTER they hire Nadia Cheikh (Responsable RH).
 *
 * Drivers carry 1–2 traits in their `personalityTraits` array (assigned at
 * data-seed time in drivers.js). Traits are immutable across the career.
 */
export const PERSONALITY_TRAITS = {
  'old-school': {
    label: 'Old school',
    description: 'Méfiant face aux nouvelles méthodes et au numérique.',
    refuses: ['ecoConduite'],
    malus: ['specMessagerie', 'gestionStress'],
    dailyImpact: {
      likes: '',
      dislikes: 'Véhicule électrique (-0,10 sat/j)',
    },
  },
  'tech-savvy': {
    label: 'Tech-savvy',
    description: 'À l\'aise avec les outils digitaux, accepte toutes les formations modernes.',
    refuses: [],
    malus: [],
    dailyImpact: {
      likes: 'Véhicule électrique (+0,10 sat/j)',
      dislikes: '',
    },
  },
  'ambitieux': {
    label: 'Ambitieux',
    description: 'Cherche à progresser vite — réclame plus souvent une augmentation.',
    refuses: [],
    malus: [],
    dailyImpact: {
      likes: 'Contrats Export EU / Spécialisé (+0,15 sat/j)',
      dislikes: '',
    },
  },
  'casanier': {
    label: 'Casanier',
    description: 'N\'aime pas les longs trajets ni les frontières.',
    refuses: ['specExport'],
    malus: [],
    dailyImpact: {
      likes: 'Messagerie / Colis (+0,10 sat/j)',
      dislikes: 'Export EU (-0,20 sat/j)',
    },
  },
  'introverti': {
    label: 'Introverti',
    description: 'Préfère travailler seul, mal à l\'aise en groupe.',
    refuses: [],
    malus: ['gestionStress'],
    dailyImpact: { likes: '', dislikes: '' },
  },
  'aventurier': {
    label: 'Aventurier',
    description: 'Goûte aux missions extrêmes et internationales.',
    refuses: [],
    malus: [],
    dailyImpact: {
      likes: 'Contrats Export EU / Spécialisé (+0,15 sat/j)',
      dislikes: '',
    },
  },
  'prudent': {
    label: 'Prudent',
    description: 'Refuse les matières dangereuses, par principe.',
    refuses: ['specADR', 'adr'],
    malus: [],
    dailyImpact: {
      likes: 'Véhicules Tier 1-2 (+0,10 sat/j)',
      dislikes: 'Contrats Spécialisé / ADR (-0,15 sat/j)',
    },
  },
  'fier': {
    label: 'Fier',
    description: 'Ne reconnaît jamais avoir besoin d\'aide.',
    refuses: ['gestionStress'],
    malus: [],
    dailyImpact: {
      likes: 'Véhicules Tier 4+ (+0,10 sat/j)',
      dislikes: 'Véhicules Tier 1 (-0,10 sat/j)',
    },
  },
  'rebelle': {
    label: 'Rebelle',
    description: 'Conteste la hiérarchie et les "bonnes pratiques".',
    refuses: ['ecoConduite'],
    malus: ['gestionStress'],
    dailyImpact: { likes: '', dislikes: '' },
  },
  'pragmatique': {
    label: 'Pragmatique',
    description: 'Accepte tout ce qui sert concrètement la mission.',
    refuses: [],
    malus: [],
    dailyImpact: { likes: '', dislikes: '' },
  },
  'perfectionniste': {
    label: 'Perfectionniste',
    description: 'Veut tout maîtriser, accepte les formations chères.',
    refuses: [],
    malus: [],
    dailyImpact: {
      likes: 'Véhicule entretenu, usure < 30 % (+0,10 sat/j)',
      dislikes: 'Véhicule en mauvais état, usure > 70 % (-0,10 sat/j)',
    },
  },
  'sceptique': {
    label: 'Sceptique',
    description: 'Doute de l\'utilité des formations courtes.',
    refuses: [],
    malus: ['ecoConduite', 'gestionStress'],
    dailyImpact: { likes: '', dislikes: '' },
  },
};

/**
 * Resolve how a driver reacts to a given training based on their traits.
 * Returns one of:
 *   { ok: true }                                  — accepts without malus
 *   { ok: true,  malus: true, trait, label }     — accepts but loses 8 sat
 *   { ok: false, refused: true, trait, label }   — refuses outright
 */
export const evaluateTrainingReaction = (driver, trainingType) => {
  const traits = driver?.personalityTraits || [];
  for (const traitId of traits) {
    const t = PERSONALITY_TRAITS[traitId];
    if (!t) continue;
    if (t.refuses?.includes(trainingType)) {
      return { ok: false, refused: true, trait: traitId, label: t.label };
    }
  }
  for (const traitId of traits) {
    const t = PERSONALITY_TRAITS[traitId];
    if (!t) continue;
    if (t.malus?.includes(trainingType)) {
      return { ok: true, malus: true, trait: traitId, label: t.label };
    }
  }
  return { ok: true };
};

/**
 * Does the driver have the 'ambitieux' trait? Used to raise the probability
 * of a "raise request" message after a level-up (from 35 % → 60 %).
 */
export const isAmbitious = (driver) =>
  (driver?.personalityTraits || []).includes('ambitieux');

/**
 * Compute the daily satisfaction delta for an *active* driver based on their
 * traits, the vehicle they drive, and the contract they execute. Applied on
 * top of the +0.2 baseline in coreSlice.nextDay.
 *
 * The amplitudes are intentionally small (±0.05 to ±0.20) so the trait drift
 * accumulates only over several weeks of mission, never overwhelming the
 * primary satisfaction levers (fatigue, raises, bonus).
 *
 * Returns the delta as a number (positive = bonus, negative = malus).
 */
export const getTraitDailyDelta = (driver, vehicle, contract) => {
  const traits = driver?.personalityTraits || [];
  if (traits.length === 0) return 0;

  // eslint-disable-next-line global-require
  const { matchSpec } = require('../utils/specialtyMatcher');

  let delta = 0;
  const isElectric = vehicle?.dataId === 'fx-electric-zeroload'
    || vehicle?.dataId?.includes('electric')
    || vehicle?.engineType === 'electric';
  const tier = vehicle?.tier || 1;
  const wear = vehicle?.wear ?? 0;
  const cat = contract?.category;
  // 04/02/2026 — Phase F3 : matching tolérant ancien/nouveau libellé.
  const isExportEU = cat && matchSpec(cat, 'Export UE');
  const isMessagerie = cat && matchSpec(cat, 'Messagerie/Colis');
  const isSpecialise = cat === 'Spécialisé' || (cat && matchSpec(cat, 'ADR/Produits sensibles'));

  for (const traitId of traits) {
    switch (traitId) {
      case 'old-school':
        if (isElectric) delta -= 0.10;
        break;
      case 'tech-savvy':
        if (isElectric) delta += 0.10;
        break;
      case 'ambitieux':
        if (isExportEU || isSpecialise) delta += 0.15;
        break;
      case 'casanier':
        if (isMessagerie) delta += 0.10;
        if (isExportEU) delta -= 0.20;
        break;
      case 'aventurier':
        if (isExportEU || isSpecialise) delta += 0.15;
        break;
      case 'prudent':
        if (tier <= 2) delta += 0.10;
        if (isSpecialise) delta -= 0.15;
        break;
      case 'fier':
        if (tier >= 4) delta += 0.10;
        if (tier === 1) delta -= 0.10;
        break;
      case 'perfectionniste':
        if (wear < 30) delta += 0.10;
        if (wear > 70) delta -= 0.10;
        break;
      default:
        break;
    }
  }
  return Math.round(delta * 100) / 100;
};

